import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';
import { LANGUAGES, LanguageCode } from '@/i18n/config';
import i18nInstance from '@/i18n/config';

interface LanguageSwitcherProps {
  className?: string;
  showLabel?: boolean;
  onLanguageChange?: (language: string) => void;
}

export default function LanguageSwitcher({
  className = '',
  showLabel = true,
  onLanguageChange,
}: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [, setForceUpdate] = React.useState(0);

  // 监听语言变化
  React.useEffect(() => {
    const onLanguageChanged = () => {
      setForceUpdate(n => n + 1);
    };
    i18nInstance.on('languageChanged', onLanguageChanged);
    return () => {
      i18nInstance.off('languageChanged', onLanguageChanged);
    };
  }, []);

  // 反向映射：'en' -> 'en-us', 'zh' -> 'zh-cn'
  const reverseLanguageMap: { [key: string]: string } = {
    en: 'en-us',
    zh: 'zh-cn',
  };
  const currentLangCode = reverseLanguageMap[i18n.language] || i18n.language;
  const currentLanguage =
    LANGUAGES.find(lang => lang.code === currentLangCode) || LANGUAGES[0];

  const handleLanguageChange = (code: LanguageCode) => {
    // 映射语言代码：'en-us' -> 'en', 'zh-cn' -> 'zh'
    const languageMap: { [key: string]: string } = {
      'en-us': 'en',
      'zh-cn': 'zh',
    };
    const mappedCode = languageMap[code] || code;

    // 使用映射后的代码更新 i18n
    i18nInstance.changeLanguage(mappedCode);
    setIsOpen(false);

    // 调用外部传入的 onLanguageChange 回调（如果存在），传递原始代码
    if (onLanguageChange) {
      onLanguageChange(code);
    }
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label="Change language"
      >
        <Globe size={18} />
        {showLabel && (
          <>
            <span className="font-medium">{currentLanguage.name}</span>
            <ChevronDown
              size={16}
              className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </>
        )}
      </button>

      {isOpen && (
        <div className="absolute left-0 top-10 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="py-1">
            {LANGUAGES.map(language => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 transition-colors ${
                  language.code === currentLanguage.code
                    ? 'bg-gray-50 text-blue-600'
                    : 'text-gray-700'
                }`}
              >
                {/* <span className="text-lg">{language.flag}</span> */}
                <span className="font-medium">{language.name}</span>
                {language.code === currentLanguage.code && (
                  <span className="ml-auto">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
